import { ErrorModal, MesfModal } from "@dexteel/mesf-core";
import { CircularProgress, Grid, Snackbar, TextField } from "@material-ui/core";
import MuiAlert from "@material-ui/lab/Alert";
import React, { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { FooterModalButtons } from "../../../../../../controls/buttons/FooterModalButtons";
import { ConfirmModal } from "../../../../../../controls/modals/ConfirmModal";
import {
	CATALOG_MOLD_SIZE_INITIAL_VALUES,
	CatalogMoldSize,
} from "../../../../models/catalogs/CatalogMoldSize";
import {
	getCatalogMoldSize,
	upsertCatalogMoldSize,
} from "../../../../repositories/CatalogMoldSizeRepository";
import { useModalStyles } from "../../../../styles/modalStyles";
import { validateNoSpecialChars } from "../../../validates";
import { useCatalogMoldSizeContext } from "../../context/CatalogMoldSizeContext";

type Props = {
	moldSizeId: number | null;
	show: boolean;
	onHide: (shouldUpdate: boolean) => void;
};

export const EditMoldSize = ({ moldSizeId, show, onHide }: Props) => {
	const { state } = useCatalogMoldSizeContext();
	const classes = useModalStyles();
	const [open, setOpen] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [isSubmitLoading, setIsSubmitLoading] = useState(false);
	const [error, setError] = useState("");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [existingMoldSize, setExistingMoldSize] = useState("");
	const {
		register,
		getValues,
		setValue,
		handleSubmit,
		formState: { errors },
	} = useForm<CatalogMoldSize>({
		defaultValues: CATALOG_MOLD_SIZE_INITIAL_VALUES,
	});

	const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
		if (reason === "clickaway") {
			return;
		}
		setOpen(false);
	};

	const onSubmit: SubmitHandler<CatalogMoldSize> = async (data) => {
		setIsSubmitLoading(true);
		const moldSizes: CatalogMoldSize[] = state.moldSizes || [];
		if (
			moldSizes.some(
				(moldSize) =>
					moldSize.MoldSizeName === data.MoldSizeName &&
					moldSize.MoldSizeId !== moldSizeId,
			)
		) {
			setExistingMoldSize(data.MoldSizeName);
			setConfirmOpen(true);
		} else {
			await performUpsert(data);
		}
	};

	const performUpsert = async (data: CatalogMoldSize) => {
		const editMoldSizeResp = await upsertCatalogMoldSize(data);
		if (editMoldSizeResp.ok) {
			setOpen(true);
			onHide(true);
		} else {
			setError(editMoldSizeResp.message);
			onHide(false);
		}
		setIsSubmitLoading(false);
	};

	const onConfirmModalHandler = async (proceed: boolean) => {
		setConfirmOpen(false);
		if (proceed) {
			await performUpsert(getValues());
		}
	};

	useEffect(() => {
		if (show) {
			setIsSubmitLoading(false);
			(async () => {
				setIsLoading(true);
				const response = await getCatalogMoldSize(moldSizeId);
				if (response.ok) {
					const moldSize = response.data.tables[0].rows[0];
					if (moldSize) {
						setValue("MoldSizeId", moldSize.MoldSizeId);
						setValue("MoldSizeName", moldSize.MoldSizeName);
						setValue("MinSpeedLimit", moldSize.MinSpeedLimit);
						setValue("LbsPerFoot", moldSize.LbsPerFoot);
						setValue("Section", moldSize.Section);
						setValue("Enabled", moldSize.IsActive);
						setIsLoading(false);
					}
				} else {
					setError(response.message);
				}
			})();
		}
	}, [show]);

	return (
		<>
			<ConfirmModal
				show={confirmOpen}
				onHide={onConfirmModalHandler}
				existingName={existingMoldSize}
				label="Mold Size"
			/>
			<MesfModal
				title="EDIT MOLD SIZE"
				open={show}
				handleClose={() => onHide(false)}
				maxWidth="sm"
				id="edit-mold-size-modal"
			>
				<form onSubmit={handleSubmit(onSubmit)}>
					<MesfModal.Content
						className={classes.modalBody}
						style={{ padding: "15px 30px" }}
						dividers
					>
						{isLoading && (
							<Grid
								container
								justifyContent="center"
								alignItems="center"
								style={{ minHeight: "200px" }}
							>
								<CircularProgress />
							</Grid>
						)}
						{!isLoading && (
							<>
								<Grid container spacing={1}>
									<Grid item xs={12}>
										<TextField
											{...register("MoldSizeName", {
												required: "Name is required",
												validate: validateNoSpecialChars,
											})}
											label="Name"
											variant="outlined"
											error={!!errors.MoldSizeName}
											fullWidth
											margin="dense"
											autoComplete="off"
										/>
										{errors.MoldSizeName && (
											<span className={classes.errorLabel}>
												{errors.MoldSizeName.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={4}>
										<TextField
											{...register("MinSpeedLimit")}
											label="Min Speed"
											variant="outlined"
											error={!!errors.MinSpeedLimit}
											type="number"
											fullWidth
											margin="dense"
											autoComplete="off"
											onFocus={(e) => e.target.select()}
											inputProps={{ min: 0 }}
										/>
										{errors.MinSpeedLimit && (
											<span className={classes.errorLabel}>
												{errors.MinSpeedLimit.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={4}>
										<TextField
											{...register("LbsPerFoot")}
											label="Lbs Per Foot"
											variant="outlined"
											error={!!errors.LbsPerFoot}
											type="number"
											fullWidth
											margin="dense"
											onFocus={(e) => e.target.select()}
											autoComplete="off"
											inputProps={{ min: 0 }}
										/>
										{errors.LbsPerFoot && (
											<span className={classes.errorLabel}>
												{errors.LbsPerFoot.message}
											</span>
										)}
									</Grid>
									<Grid item xs={12} md={4}>
										<TextField
											{...register("Section")}
											label="Section"
											variant="outlined"
											error={!!errors.Section}
											type="number"
											fullWidth
											margin="dense"
											onFocus={(e) => e.target.select()}
											autoComplete="off"
											inputProps={{ min: 0 }}
										/>
										{errors.Section && (
											<span className={classes.errorLabel}>
												{errors.Section.message}
											</span>
										)}
									</Grid>
								</Grid>
							</>
						)}
					</MesfModal.Content>
					<MesfModal.Actions style={{ padding: "20px 30px 30px" }}>
						<FooterModalButtons
							isSubmitLoading={isSubmitLoading}
							onHide={onHide}
						/>
					</MesfModal.Actions>
				</form>
			</MesfModal>
			<Snackbar open={open} autoHideDuration={2500} onClose={handleClose}>
				<MuiAlert
					elevation={6}
					variant="filled"
					severity="success"
					onClose={handleClose}
				>
					The mold size was edited successfully
				</MuiAlert>
			</Snackbar>
			<ErrorModal
				error={error}
				onHide={() => setError("")}
				title="Error Editing Mold Size"
			/>
		</>
	);
};
